import { router } from "@inertiajs/react";
import { useState } from "react";
import Header from "../Component/Header";

const getCurrentLocalDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function Index({ data = [], filters = {} }) {
    const [dateFrom, setDateFrom] = useState(filters?.date_from ?? getCurrentLocalDate());
    const [dateTo, setDateTo] = useState(filters?.date_to ?? getCurrentLocalDate());

    const statusBadgeClass = (status) => {
        const normalized = String(status || "").toLowerCase();

        if (normalized === "success" || normalized === "berhasil") {
            return "bg-green-100 text-green-700";
        }

        if (normalized === "pending") {
            return "bg-amber-100 text-amber-700";
        }

        if (normalized === "expired" || normalized === "failed") {
            return "bg-red-100 text-red-700";
        }

        return "bg-slate-100 text-slate-700";
    };

    const applyDateFilter = (event) => {
        event.preventDefault();

        router.get(
            "/panel/invoices",
            {
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
            },
            {
                preserveState: true,
                preserveScroll: true,
                replace: true,
            }
        );
    };

    const resetDateFilter = () => {
        const today = getCurrentLocalDate();
        setDateFrom(today);
        setDateTo(today);
        router.get(
            "/panel/invoices",
            {
                date_from: today,
                date_to: today,
            },
            {
                preserveState: true,
                preserveScroll: true,
                replace: true,
            }
        );
    };

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div className="flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <h1 className="text-lg md:text-xl font-semibold text-foreground">Invoice</h1>
                                <p className="text-sm text-muted-foreground-1">List of invoices, payment status, and momo transfer status.</p>
                            </div>
                        </div>

                        <div className="bg-card border border-card-line rounded-xl shadow-2xs p-5">
                            <form onSubmit={applyDateFilter} className="mb-4 flex flex-wrap items-end gap-2">
                                <div>
                                    <label className="block mb-1 text-xs text-muted-foreground-1">From</label>
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(event) => setDateFrom(event.target.value)}
                                        className="py-2 px-3 block bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-xs text-muted-foreground-1">To</label>
                                    <input
                                        type="date"
                                        value={dateTo}
                                        onChange={(event) => setDateTo(event.target.value)}
                                        className="py-2 px-3 block bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-primary border border-primary-line text-primary-foreground hover:bg-primary-hover"
                                >
                                    Apply
                                </button>
                                <button
                                    type="button"
                                    onClick={resetDateFilter}
                                    className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground hover:bg-layer-hover"
                                >
                                    Reset
                                </button>
                            </form>

                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-table-line">
                                    <thead>
                                        <tr>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Invoice Code</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Sales Order</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Payment</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Payment Method</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Payment Status</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Momo Status</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-table-line">
                                        {data.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-6 px-4 text-center text-sm text-muted-foreground-2">
                                                    No invoices found.
                                                </td>
                                            </tr>
                                        ) : (
                                            data.map((invoice) => (
                                                <tr key={invoice.inv_id} className="hover:bg-surface">
                                                    <td className="py-3 px-4 text-sm text-foreground">{invoice.inv_code}</td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">{invoice.sales_order?.so_code ?? "-"}</td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">{invoice.payment?.pay_code ?? "-"}</td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">{invoice.payment?.pay_method_nama ?? "-"}</td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(invoice.payment?.payment_status_label)}`}>
                                                            {invoice.payment?.payment_status_label ?? "-"}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(invoice.status_momo_transfer_label)}`}>
                                                            {invoice.status_momo_transfer_label ?? "-"}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => router.visit(`/panel/invoices/${encodeURIComponent(invoice.inv_code ?? "")}`)}
                                                            className="py-1.5 px-2.5 inline-flex items-center gap-x-2 text-xs font-medium rounded-lg border border-layer-line bg-layer text-layer-foreground hover:bg-layer-hover"
                                                        >
                                                            Detail
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
